import { useState } from "react";
import { motion } from "framer-motion";
import { 
  Heart, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Briefcase,
  Clock,
  CheckCircle,
  Users,
  Headphones,
  FileText,
  Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const volunteerRoles = [
  { value: "helpline", label: "Helpline Support", icon: Headphones, description: "Answer calls and guide victims" },
  { value: "documentation", label: "Documentation Help", icon: FileText, description: "Help seniors fill forms and reports" },
  { value: "awareness", label: "Awareness Campaigns", icon: Users, description: "Spread awareness in communities" },
  { value: "translation", label: "Translation Services", icon: Globe, description: "Help with regional language support" },
  { value: "tech", label: "Tech Support", icon: Briefcase, description: "Help with website and app development" },
];

const availabilityOptions = [
  { value: "weekdays", label: "Weekdays (Mon-Fri)" },
  { value: "weekends", label: "Weekends (Sat-Sun)" },
  { value: "evenings", label: "Evenings Only" },
  { value: "flexible", label: "Flexible Schedule" },
];

const hoursOptions = [
  { value: "2-5", label: "2-5 hours/week" },
  { value: "5-10", label: "5-10 hours/week" },
  { value: "10-20", label: "10-20 hours/week" },
  { value: "20+", label: "20+ hours/week" },
];

const VolunteerSignup = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    occupation: "",
    role: "",
    availability: "",
    hours: "",
    experience: "",
    motivation: "",
    agreeTerms: false,
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.role) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (!formData.agreeTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city || null,
        occupation: formData.occupation || null,
        role: formData.role,
        availability: formData.availability || null,
        hours: formData.hours || null,
        experience: formData.experience || null,
        motivation: formData.motivation || null,
      };
      
      await axios.post(`${API}/volunteers`, payload);
      setIsSubmitted(true);
      toast.success("Thank you for volunteering! We'll contact you soon.");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] py-12" data-testid="volunteer-success">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="text-center shadow-lg border-t-4 border-t-[#27ae60]">
              <CardContent className="p-8 md:p-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-[#27ae60]" />
                </div>
                
                <h1 className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                  Thank You for Volunteering!
                </h1>
                
                <p className="text-lg text-[#546e7a] mb-6">
                  Your application has been received. Our team will review your information and contact you within 3-5 business days.
                </p>
                
                <div className="bg-slate-50 rounded-lg p-6 text-left mb-8">
                  <h3 className="font-bold text-[#2c3e50] mb-3">What happens next?</h3>
                  <ul className="space-y-2 text-[#546e7a]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#27ae60]">1.</span>
                      Our volunteer coordinator will review your application
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#27ae60]">2.</span>
                      You'll receive an email with orientation details
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#27ae60]">3.</span>
                      Complete a brief online training session
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#27ae60]">4.</span>
                      Start making a difference in seniors' lives!
                    </li>
                  </ul>
                </div>
                
                <Button
                  onClick={() => window.location.href = "/"}
                  className="bg-[#2c3e50] hover:bg-[#1a252f] text-white text-lg font-bold py-4 px-8 rounded-lg"
                >
                  Return to Home
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8 md:py-12" data-testid="volunteer-signup-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-[#e74c3c]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-[#e74c3c]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope'] mb-2">
            Become a Volunteer
          </h1>
          <p className="text-lg text-[#546e7a] max-w-2xl mx-auto">
            Join our mission to protect senior citizens from financial fraud. Your time and skills can make a real difference.
          </p>
        </motion.div>

        {/* Volunteer Roles */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <h2 className="text-xl font-bold text-[#2c3e50] font-['Manrope'] mb-4 text-center">
            How You Can Help
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {volunteerRoles.map((role) => (
              <Card 
                key={role.value} 
                className={`cursor-pointer transition-all ${
                  formData.role === role.value 
                    ? "border-2 border-[#e74c3c] bg-red-50" 
                    : "hover:shadow-md"
                }`}
                onClick={() => updateFormData("role", role.value)}
              >
                <CardContent className="p-4 text-center">
                  <role.icon className={`w-8 h-8 mx-auto mb-2 ${
                    formData.role === role.value ? "text-[#e74c3c]" : "text-[#2c3e50]"
                  }`} />
                  <h3 className="font-semibold text-[#2c3e50] text-sm">{role.label}</h3>
                  <p className="text-xs text-[#546e7a] mt-1">{role.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Signup Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="shadow-lg border-t-4 border-t-[#e74c3c]">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                Volunteer Registration Form
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#2c3e50] flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Personal Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        Full Name <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={(e) => updateFormData("name", e.target.value)}
                        className="input-senior"
                        data-testid="volunteer-name"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        Email Address <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <Input
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => updateFormData("email", e.target.value)}
                        className="input-senior"
                        data-testid="volunteer-email"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        Phone Number <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <Input
                        type="tel"
                        placeholder="10-digit mobile number"
                        value={formData.phone}
                        onChange={(e) => updateFormData("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                        className="input-senior"
                        data-testid="volunteer-phone"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        City
                      </Label>
                      <Input
                        type="text"
                        placeholder="Your city"
                        value={formData.city}
                        onChange={(e) => updateFormData("city", e.target.value)}
                        className="input-senior"
                        data-testid="volunteer-city"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                      Current Occupation
                    </Label>
                    <Input
                      type="text"
                      placeholder="e.g., Software Engineer, Retired Teacher, Student"
                      value={formData.occupation}
                      onChange={(e) => updateFormData("occupation", e.target.value)}
                      className="input-senior"
                      data-testid="volunteer-occupation"
                    />
                  </div>
                </div>

                {/* Availability */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#2c3e50] flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Availability
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        When are you available?
                      </Label>
                      <Select value={formData.availability} onValueChange={(value) => updateFormData("availability", value)}>
                        <SelectTrigger className="input-senior" data-testid="volunteer-availability">
                          <SelectValue placeholder="Select availability" />
                        </SelectTrigger>
                        <SelectContent>
                          {availabilityOptions.map(option => (
                            <SelectItem key={option.value} value={option.value} className="text-base py-3">
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        Hours you can commit
                      </Label>
                      <Select value={formData.hours} onValueChange={(value) => updateFormData("hours", value)}>
                        <SelectTrigger className="input-senior" data-testid="volunteer-hours">
                          <SelectValue placeholder="Select hours per week" />
                        </SelectTrigger>
                        <SelectContent>
                          {hoursOptions.map(option => (
                            <SelectItem key={option.value} value={option.value} className="text-base py-3">
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Experience & Motivation */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                      Relevant Experience (Optional)
                    </Label>
                    <Textarea
                      placeholder="Share any relevant experience (e.g., previous volunteer work, skills with seniors, language skills)"
                      value={formData.experience}
                      onChange={(e) => updateFormData("experience", e.target.value)}
                      className="min-h-[100px] text-base"
                      data-testid="volunteer-experience"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                      Why do you want to volunteer? (Optional)
                    </Label>
                    <Textarea
                      placeholder="Tell us what motivates you to help senior citizens"
                      value={formData.motivation}
                      onChange={(e) => updateFormData("motivation", e.target.value)}
                      className="min-h-[100px] text-base"
                      data-testid="volunteer-motivation"
                    />
                  </div>
                </div>

                {/* Terms */}
                <div className="flex items-start space-x-3 bg-slate-50 p-4 rounded-lg">
                  <Checkbox
                    id="terms"
                    checked={formData.agreeTerms}
                    onCheckedChange={(checked) => updateFormData("agreeTerms", checked)}
                    className="mt-1"
                    data-testid="volunteer-terms"
                  />
                  <Label htmlFor="terms" className="text-base text-[#546e7a] cursor-pointer">
                    I agree to the volunteer terms and conditions. I understand that volunteering is unpaid and I commit to maintaining confidentiality of any sensitive information I may encounter.
                  </Label>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#e74c3c] hover:bg-[#c0392b] text-white text-lg font-bold py-4 rounded-lg shadow-lg"
                  data-testid="volunteer-submit"
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default VolunteerSignup;
